import React, { createContext, useCallback, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import {
  sendPerformanceEvent as sendPerfEvent,
  sendPerformanceMeasure as sendPerfMeasure
} from "../../orca/utils/sendPerformance";
import { useAdsElementsRefContext } from "./AdsElementsRefContext";
import { useAutoplayContext } from "./AutoplayContext";

const VideoPerformanceContext = createContext({
  sendAnalyticsEvent: () => {},
  avAdMeta: null,
  setAvAdMeta: () => {},
  adAttribute: null,
  setAdAttribute: () => {},
  firstVideoLoaded: false
});

export const usePerformanceContext = () => useContext(VideoPerformanceContext);

export const VideoPerformanceProvider = ({ children, config = {} }) => {
  const { adWrapperRef } = useAdsElementsRefContext();
  const { autoplayState } = useAutoplayContext();
  const { debug, abVariant, isPreroll = false } = config;

  const sendPerformanceEvent = useCallback(
    (performanceEvent, detail) => {
      sendPerfEvent(performanceEvent, debug, {
        ...detail,
        ...(abVariant && { abVariant }),
        isPreroll: isPreroll.toString(),
        isAutoplay: autoplayState.isAutoplay.toString(),
        nodeId: adWrapperRef.current?.id
      });
    },
    [debug, adWrapperRef, abVariant, isPreroll, autoplayState.isAutoplay]
  );

  const sendPerformanceMeasure = useCallback(
    (performanceEvent, prevEvent, detail) => {
      sendPerfMeasure(performanceEvent, prevEvent, debug, {
        ...detail,
        ...(abVariant && { abVariant }),
        isPreroll: isPreroll.toString(),
        isAutoplay: autoplayState.isAutoplay.toString(),
        nodeId: adWrapperRef.current?.id
      });
    },
    [debug, adWrapperRef, abVariant, autoplayState.isAutoplay, isPreroll]
  );

  const contextValue = useMemo(
    () => ({
      sendPerformanceEvent,
      sendPerformanceMeasure
    }),
    [sendPerformanceEvent, sendPerformanceMeasure]
  );

  return (
    <VideoPerformanceContext.Provider value={contextValue}>
      {children}
    </VideoPerformanceContext.Provider>
  );
};

VideoPerformanceProvider.propTypes = {
  config: PropTypes.object,
  children: PropTypes.node
};
